<script setup>
import AppLayout from "@/Layouts/AppLayout.vue";
import InputError from "@/Components/InputError.vue";
import InputLabel from "@/Components/InputLabel.vue";
import FormSection from "@/Components/FormSection.vue";
import TextInput from "@/Components/TextInput.vue";
import { useForm, usePage } from "@inertiajs/inertia-vue3";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import { useHelpers } from "@/helpers";
import { computed, ref, watch } from "vue";

const props = defineProps({
  vendor: Object
});

const currentUser = usePage().props.value.auth.user;
const vendorProp = computed(() => usePage().props.value.vendor);
const helpers = useHelpers();

const saving = ref(false);
const form = useForm({
  _method: 'POST',
  vendorName: props.vendor.vendorName || null,
  address: props.vendor.address || null,
  city: props.vendor.city || null,
  state: props.vendor.state || null,
  zip: props.vendor.zip || null,
  phone: props.vendor.phone || null,
  cell: props.vendor.cell || null,
  email: props.vendor.email || null,
  www: props.vendor.www || null,
  country: props.vendor.country || 'USA',
  notes: props.vendor.notes || null,
  accountNumber: props.vendor.accountNumber || null,
  username: props.vendor.username || null,
  password: props.vendor.password || null,
  sales_rep: props.vendor.sales_rep || null,
  vendor_code: props.vendor.vendor_code || null,
});

watch(vendorProp, (vendorData) => {
  console.log(vendorData);
  if (Object.keys(usePage().props.value.errors).length > 0) {
    return;
  }

  Object.keys(form.data()).forEach(key => {
    form[key] = vendorData[key];
    console.log(key, vendorData[key]);
  });
}, { deep: true });
const saveForm = () => {
  saving.value = true;
  let endpoint = route('admin.vendors.store');
  if (props.vendor.vendorID) {
    endpoint = route('admin.vendors.update', { vendor: props.vendor.vendorID });
    form._method = 'PUT';
  }
  if (form.www) {
    form.www = form.www.trim().toLowerCase();
    if (!form.www.startsWith('http')) {
      form.www = `https://${form.www}`;
    }
  }

  form.post(endpoint, {
    preserveScroll: true,
    onFinish() {
      saving.value = false;
    }
  })
};
</script>

<template>
  <AppLayout title="Edit Vendor">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        <span v-if="vendor.vendorID">Edit</span><span v-else>Create</span> Vendor: <b v-text="vendor.vendorName"></b>
      </h2>
    </template>

    <div class="max-w-7xl mx-auto py-10 sm:px-6 lg:px-8">
      <FormSection class="mt-5">
        <template #title>
          Contact information
        </template>
        <template #description>
          These are the basic information for the user
        </template>
        <template #form>
          <!-- Vendor name -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="vendorName" value="Vendor Name" :required="true" />
            <TextInput id="vendorName" v-model="form.vendorName" type="text" class="mt-1 block w-full"
              autocomplete="vendorName" :disabled="!helpers.userHasPermission(currentUser, 'vendor_database')" />
            <InputError :message="form.errors.vendorName" class="mt-2" />
          </div>
          <!-- Vendor Code -->
          <div class="col-span-6 sm:col-span-4 relative group">
            <InputLabel for="vendor_code" value="Vendor Code" />
            <TextInput id="vendor_code" v-model="form.vendor_code" type="text" class="mt-1 block w-full"
              autocomplete="off" :disabled="!helpers.userHasPermission(currentUser, 'admin')" />

            <div v-if="!helpers.userHasPermission(currentUser, 'admin')"
              class="absolute left-0 top-full mt-1 hidden group-hover:block bg-gray-800 text-white text-xs px-2 py-1 rounded shadow-lg z-10">
              Only users with admin permissions can edit the vendor code.
            </div>

            <InputError :message="form.errors.vendor_code" class="mt-2" />
          </div>
          <!-- Address -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="address" value="Address" :required="true" />
            <TextInput id="address" v-model="form.address" type="text" class="mt-1 block w-full"
              autocomplete="address" />
            <InputError :message="form.errors.address" class="mt-2" />
          </div>
          <!-- City -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="city" value="City" :required="true" />
            <TextInput id="city" v-model="form.city" type="text" class="mt-1 block w-full" autocomplete="city" />
            <InputError :message="form.errors.city" class="mt-2" />
          </div>
          <!-- State -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="state" value="State" :required="true" />
            <TextInput id="state" v-model="form.state" type="text" class="mt-1 block w-full" autocomplete="state" />
            <InputError :message="form.errors.state" class="mt-2" />
          </div>
          <!-- Zip -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="zip" value="Zip" :required="true" />
            <TextInput id="zip" v-model="form.zip" type="text" class="mt-1 block w-full" autocomplete="zip" />
            <InputError :message="form.errors.zip" class="mt-2" />
          </div>
          <!-- Country -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="country" value="Country" :required="true" />
            <TextInput id="country" v-model="form.country" type="text" class="mt-1 block w-full"
              autocomplete="country" />
            <InputError :message="form.errors.country" class="mt-2" />
          </div>
          <!-- Phone -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="phone" value="Phone" :required="true" />
            <TextInput id="phone" v-model="form.phone" type="text" class="mt-1 block w-full" autocomplete="phone" />
            <InputError :message="form.errors.phone" class="mt-2" />
          </div>
          <!-- Cellular -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="cell" value="Cellular" :required="true" />
            <TextInput id="cell" v-model="form.cell" type="text" class="mt-1 block w-full" autocomplete="cell" />
            <InputError :message="form.errors.cell" class="mt-2" />
          </div>
          <!-- Email -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="email" value="Email" :required="true" />
            <TextInput id="email" v-model="form.email" type="text" class="mt-1 block w-full" autocomplete="email" />
            <InputError :message="form.errors.email" class="mt-2" />
          </div>
          <!-- URL -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="www" value="URL" :required="true" />
            <TextInput id="www" v-model="form.www" type="text" class="mt-1 block w-full" autocomplete="www" />
            <InputError :message="form.errors.www" class="mt-2" />
          </div>
        </template>
      </FormSection>

      <FormSection class="mt-5">
        <template #title>
          Account information
        </template>
        <template #description>
          These are the basic information for the user
        </template>
        <template #form>
          <!-- Account Number -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="accountNumber" value="Account Number" :required="true" />
            <TextInput id="accountNumber" v-model="form.accountNumber" type="text" class="mt-1 block w-full"
              autocomplete="accountNumber" />
            <InputError :message="form.errors.accountNumber" class="mt-2" />
          </div>
          <!-- Sales Rep -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="sales_rep" value="Sales Rep" :required="true" />
            <TextInput id="sales_rep" v-model="form.sales_rep" type="text" class="mt-1 block w-full"
              autocomplete="sales_rep" />
            <InputError :message="form.errors.sales_rep" class="mt-2" />
          </div>
          <!-- Username -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="username" value="Username" :required="true" />
            <TextInput id="username" v-model="form.username" type="text" class="mt-1 block w-full"
              autocomplete="username" />
            <InputError :message="form.errors.username" class="mt-2" />
          </div>
          <!-- Password -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="password" value="Password" :required="true" />
            <TextInput id="password" v-model="form.password" type="text" class="mt-1 block w-full"
              autocomplete="password" />
            <InputError :message="form.errors.password" class="mt-2" />
          </div>
          <!-- Notes -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="notes" value="Notes" :required="true" />
            <textarea rows="4" id="notes" v-model="form.notes"
              class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm mt-1 block w-full disabled:bg-gray-50"
              :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"></textarea>
            <InputError :message="form.errors.notes" class="mt-2" />
          </div>
        </template>
        <template #actions>
          <a :href="route('admin.vendors.active')" class="mr-5" v-if="!saving">
            <SecondaryButton type="button">Back</SecondaryButton>
          </a>
          <PrimaryButton type="button" @click.prevent="saveForm" :disabled="saving">Save</PrimaryButton>
        </template>
      </FormSection>

      <FormSection class="mt-5" v-if="vendor.vendorID">
        <template #title>
          Vendor Contacts
        </template>
        <template #description>
          Users related to the vendor with the ability to login
        </template>
        <template #form>
          <div class="col-span-6">
            <p v-if="vendor.users.length === 0">No users related to this vendor
            </p>
            <div v-else>
              <p v-for="user in vendor.users"><span v-text="helpers.fullName(user)"></span>
                <span class="ml-2 font-semibold" v-text="`(${user.email})`"></span>
                <a :href="route('admin.users.edit', { user: user.employeeID })" class="ml-2" target="_blank"
                  ref="noopener noreferrer">
                  <SecondaryButton>Edit</SecondaryButton>
                </a>
              </p>
            </div>
            <a :href="route('admin.users.create', { userType: 1, employeeTypeId: vendor.vendorID })" target="_blank"
              ref="noopener noreferrer">
              <SecondaryButton>Create Vendor Contact</SecondaryButton>
            </a>
          </div>
        </template>
      </FormSection>
    </div>
  </AppLayout>
</template>
